import { useEffect, useState } from "react"
import { TriangleAlert } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslation } from "react-i18next"

import {
  Button,
  Dialog,
  DialogContent,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@f2e/ui/components/base"
import { AutosizeTextarea } from "@f2e/ui/components/base-plus"

import { useToast } from "@/hooks"

interface ConversationEditDialogProps {
  open?: boolean
  question: string
  onOpenChange: (open: boolean) => void
  onConfirm: (questionContent: string) => void
}

const formSchema = z.object({
  question: z.string().min(1),
})

type FormInput = z.infer<typeof formSchema>

const ConversationEditDialog = ({
  open = false,
  question,
  onOpenChange,
  onConfirm,
}: ConversationEditDialogProps) => {
  const { t } = useTranslation()

  const form = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    values: {
      question,
    },
  })
  const { alertToast } = useToast()
  const [show, setShow] = useState(false)

  useEffect(() => {
    setShow(open || false)
  }, [open])

  const handleConfirmClick = (formData: FormInput) => {
    if (formData.question === question) {
      alertToast({
        title: t("history.editDialog.alert.title"),
        description: t("history.editDialog.alert.noChange"),
      })
      return
    }

    if (formData.question.trim() === "") {
      alertToast({
        title: t("history.editDialog.alert.title"),
        description: t("history.editDialog.validation.empty"),
      })
      return
    }

    onConfirm?.(formData.question)
    form.reset()
  }

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    if (!open) {
      setShow(false)
      form.reset()
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.shiftKey && e.key === "Enter") {
      return
    }

    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault()
      form.handleSubmit(handleConfirmClick)()
    }
  }

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {t("history.conversationFooter.edit.title")}
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-1 pt-2 text-sm text-yellow-600 dark:text-yellow-500">
            <span className="flex items-center">
              <TriangleAlert className="w-4 h-4 inline mr-1" />
              <span>{t("history.conversationFooter.edit.warning")}</span>
            </span>
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="rounded-lg border bg-background focus-within:ring-1 focus-within:ring-ring">
            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AutosizeTextarea
                      {...field}
                      placeholder={t("history.editDialog.placeholder")}
                      className="resize-none rounded-none border-0 p-3 shadow-none focus-visible:ring-0 text-xl"
                      minHeight={100}
                      maxHeight={300}
                      onKeyDown={handleInputKeyDown}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("history.editDialog.cancel")}
            </Button>
          </DialogClose>
          <Button type="submit" onClick={form.handleSubmit(handleConfirmClick)}>
            {t("history.conversationFooter.actions.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ConversationEditDialog
